import { Typography } from "@vds/typography";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCVAppLocationData } from "../../global/utility/Utils";
import { patchWidgetConfig } from "../../store/actions";
import EmptyWidget from "./EmptyWidget";
import StatWidget from "./StatWidget";
import { WidgetGridContainer, WidgetHeader, WidgetLayout } from "./styles";
import WidgetSettings from "./WidgetSettings";

function WidgetContainer({ onDrop, children, onDragEnter, onDragLeave, isDraggedOver, size }) {
  return (
    <div
      style={
        isDraggedOver
          ? {
              border: "dashed 2px #abcdef",
              borderRadius: "5px",
              minHeight: "5rem",
              position: "relative",
              boxSizing: "border-box",
              gridColumn: `span ${size}`
            }
          : { gridColumn: `span ${size}`, position: "relative" }
      }
      onDrop={onDrop}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={(e) => e.preventDefault()}
    >
      {!isDraggedOver && children}
    </div>
  );
}

const WidgetsContainer = () => {
  const widgetConfig = useSelector((state) => state.widgetReducer.widgetConfig);
  const cVappList = useSelector((state) => state.cvAppReducer.cvAppList || []);
  const dispatch = useDispatch();
  const [widgets, setWidgets] = useState(widgetConfig);
  const [draggedItemId, setDraggedItemId] = useState(null);
  const [draggedOverContainerId, setDraggedOverContainerId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const nonEmptyWidgets = widgets?.filter((w) => !w?.isEmpty);
  const handleDragStart = (id) => setDraggedItemId(id);
  const handleDragEntered = (id) => setDraggedOverContainerId(id);
  const handleDragLeave = () => setDraggedOverContainerId(null);
  const { activeSession } = useSelector((state) => state.sessionReducer.activeSession);

  // Detect mobile for disabling drag-and-drop
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const isDragEnabled = !isMobile && editMode;

  const handleDrop = () => {
    if (!isDragEnabled || !draggedOverContainerId) {
      clearState();
      return;
    }

    const fromIndex = widgets.findIndex((w) => w.id === draggedItemId);
    const toIndex = widgets.findIndex((w) => w.id === draggedOverContainerId);
    setWidgets((w) => moveItem(w, fromIndex, toIndex));

    clearState();
  };

  const clearState = () => {
    setDraggedItemId(null);
    setDraggedOverContainerId(null);
  };

  const saveClicked = () => {
    dispatch(
      patchWidgetConfig({
        customdata: {
          widgetconfig: widgets
        }
      })
    );
  };

  const clearClicked = (id) => {
    let filteredWidgets = widgetConfig.filter((x) => x.id !== id);
    dispatch(
      patchWidgetConfig({
        customdata: {
          widgetconfig: filteredWidgets
        }
      })
    );
  };

  const fetchDataAndUpdateWidgets = async () => {
    const appIds = [...new Set(widgetConfig.map((w) => w.content.appName))];
    const appData = await Promise.all(appIds.map((appId) => getCVAppLocationData(appId, activeSession)));

    const updatedWidgets = widgetConfig.filter((widget) =>
      appData[appIds.indexOf(widget.content.appName)]?.some((data) => data.locationids?.includes(widget.content.location))
    );
    dispatch(patchWidgetConfig({ customdata: { widgetconfig: updatedWidgets } }));
  };

  useEffect(() => {
    //fetchDataAndUpdateWidgets();
  }, []);

  useEffect(() => {
    if (!widgetConfig || widgetConfig.length === 0) {
      setWidgets([]);
    } else {
      if(cVappList?.length > 0 && widgetConfig?.length > 0){
      setWidgets(widgetConfig);
      }
    }
  }, [widgetConfig?.length, cVappList]);

  return (
    <>
      <WidgetLayout>
        <WidgetHeader>
          <Typography sx={{ fontWeight: 400, fontSize: "16px" }}>STATISTICS</Typography>
          <WidgetSettings setEditMode={setEditMode} saveClicked={saveClicked} />
        </WidgetHeader>
        <WidgetGridContainer>
          {nonEmptyWidgets.map((w) => {
            return (
              <WidgetContainer
                key={w.id}
                onDrop={isDragEnabled ? handleDrop : undefined}
                onDragEnter={isDragEnabled ? () => handleDragEntered(w.id) : undefined}
                onDragLeave={isDragEnabled ? handleDragLeave : undefined}
                isDraggedOver={isDragEnabled && w.id === draggedOverContainerId}
                size={w.size}
              >
                <StatWidget
                  onDragStart={isDragEnabled ? () => handleDragStart(w.id) : undefined}
                  content={w.content}
                  editMode={editMode}
                  id={w.id}
                  clearClicked={clearClicked}
                  draggable={isDragEnabled}
                />
              </WidgetContainer>
            );
          })}
          <WidgetContainer
            key={"emptyContainer"}
            onDrop={handleDrop}
            onDragEnter={() => handleDragEntered("emptyContainer")}
            onDragLeave={handleDragLeave}
            isDraggedOver={false}
            size={1}
          >
            <EmptyWidget index={nonEmptyWidgets.length} />
          </WidgetContainer>
        </WidgetGridContainer>
      </WidgetLayout>
    </>
  );
};

export function moveItem(list, from, to) {
  const listClone = [...list];
  if (from < to) {
    listClone.splice(to + 1, 0, listClone[from]);
    listClone.splice(from, 1);
  } else if (to < from) {
    listClone.splice(to, 0, listClone[from]);
    listClone.splice(from + 1, 1);
  }
  return listClone;
}

// export function moveItemOriginal(list, from, to) {
//   console.log(`Swapping from ${from} to ${to}`);
//   if (from < to) {
//     return [
//       ...list.slice(0, from),
//       ...list.slice(from + 1, to + 1),
//       list[from],
//       ...list.slice(to + 1),
//     ];
//   } else if (to < from) {
//     return [
//       ...list.slice(0, to),
//       list[from],
//       ...list.slice(to, from),
//       ...list.slice(from + 1),
//     ];
//   }
//   return [...list];
// }

export default WidgetsContainer;
